import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { ChangeEventHandler, useEffect } from 'react';
import useToast from 'hooks/useToast';
import _ from 'lodash';
import { useGetSearchRecommendQuery } from 'hooks/queries/search';
import useCommonStore from 'store/useCommonStore';

/** input창에 키보드를 입력할 때 발생할 모든 동작들 */
const useSearchKeydown = (search_dom: HTMLElement | null) => {
	// router
	const navigate = useNavigate();
	const [search_params] = useSearchParams();
	const keyword_query = search_params.get('keyword');
	// toast
	const toast = useToast();
	// store
	const {
		keyword,
		setKeyword,
		selected_recommend,
		setSelectedRecommend,
		search_modal,
		setSearchModal,
	} = useCommonStore();

	// query
	const { data = [] } = useGetSearchRecommendQuery(keyword);

	/** input 창에 값을 입력할 때 마다 */
	const searchChange: ChangeEventHandler<HTMLInputElement> = (e) => {
		setKeyword(e.currentTarget.value);
		if (keyword.trim() !== '') setSelectedRecommend({ text: keyword, idx: 0 });
		else if (keyword.trim() === '') setSelectedRecommend({ text: '', idx: null });
	};

	/** 텍스트를 입력하는 것 외에 input 창에서 키보드를 눌렀을 때 발생할 이벤트 */
	useEffect(() => {
		if (search_dom === null) return;

		/** ArrowDown, ArrowUp, Enter 시에 각각 발생할 동작 */
		const keydown_value: Record<string, () => void> = {
			ArrowDown() {
				if (selected_recommend.idx === data.length)
					setSelectedRecommend({ text: '', idx: null });
				else if (selected_recommend.idx === null)
					setSelectedRecommend({ text: keyword, idx: 0 });
				else
					setSelectedRecommend({
						text: data[selected_recommend.idx],
						idx: selected_recommend.idx + 1,
					});
			},
			ArrowUp() {
				if (selected_recommend.idx === 0) setSelectedRecommend({ text: '', idx: null });
				else if (
					selected_recommend.idx === 1 ||
					(selected_recommend.idx === null && data.length === 0)
				)
					setSelectedRecommend({ text: keyword, idx: 0 });
				else if (selected_recommend.idx === null && data.length > 0)
					setSelectedRecommend({ text: data[data.length - 1], idx: data.length });
				else if (selected_recommend.idx !== null)
					setSelectedRecommend({
						text: data[selected_recommend.idx - 2],
						idx: selected_recommend.idx - 1,
					});
			},
			Enter() {
				if (keyword.trim() === '') toast('공백을 제외하고 한 글자 이상입력해주세요');

				if (selected_recommend.idx === null && keyword !== '') {
					setSearchModal(false);
					navigate(`/imms/search?keyword=${encodeURIComponent(keyword)}`, {
						replace: true,
					});
				} else if (selected_recommend.idx === 0) {
					setSearchModal(false);
					navigate(`/imms/search?keyword=${encodeURIComponent(keyword)}`, {
						replace: true,
					});
				} else if (selected_recommend.idx !== null) {
					setSearchModal(false);
					setKeyword(selected_recommend.text);
					navigate(
						`/imms/search?keyword=${encodeURIComponent(selected_recommend.text)}`,
						{
							replace: true,
						},
					);
					setSelectedRecommend({ text: '', idx: null });
				} else return;
			},
		};

		const keydownEvent = (e: KeyboardEvent) => {
			const target = ['ArrowDown', 'ArrowUp', 'Enter'];

			if (target.includes(e.key)) keydown_value[e.key]();
		};

		search_dom.addEventListener('keydown', keydownEvent);

		return () => search_dom.removeEventListener('keydown', keydownEvent);
	}, [data, search_dom, selected_recommend, keyword, keyword_query, search_modal]);

	return searchChange;
};

export default useSearchKeydown;
